// Utils Should not include a computed, just functions
import { useConstituentRiskAnalysisSteps } from '@/composables/useAnalysisSteps';
import { useConstituentRiskUtilities } from '@/composables/useConstituentRiskUtilities';
import {
  ExposureConstants,
  FilterConstants,
  RiskTableTabs,
  RiskTabs,
  TableLayerConstants,
} from '@/types/analytics/ConstituentRisk';
import { DEFAULT_DECIMAL_PLACES } from '@/constants/NumberConstants';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import usePortfolioTree from '@/composables/usePortfolioTree';
import { Currency, getCurrencySymbol } from '@/constants/Currency';
import { useRiskPreferences } from '@/composables/useRiskPreferences';
import useColorMap from '@/composables/useColorMap';
import { findTreeComponentByStrategyCode } from './portfolioTree';
import { SettingsModule } from '@/store/modules/settings';
import { useItemUnderAnalysisGlobalState } from '@/composables/useItemUnderAnalysis';
import { useFeatureFlag } from '@/composables/useFeatureFlag';

/**
 * Risk Underlying needs linear_risk_metrics_v2 / ClickHouse. Drop a persisted layer when the
 * admin toggle (i.e. `shouldUseClickhouse`) is off so risk and position tables do not request child rows the API cannot fill.
 */
export const excludeDeltaUnderlyingUnlessClickhouse = (
  layers: TableLayerConstants[],
  shouldUseClickhouse: boolean,
): TableLayerConstants[] =>
  shouldUseClickhouse ? layers : layers.filter((layer) => layer !== TableLayerConstants.RISK_UNDERLYING);

/** Delta Call pivot tables (columns are bucket labels, not a group-by layer). */
export const DELTA_CALL_BUCKET_TABLES: readonly RiskTableTabs[] = [
  RiskTableTabs.DELTA_BY_DELTA_CALL,
  RiskTableTabs.GAMMA_BY_DELTA_CALL,
  RiskTableTabs.VEGA_BY_DELTA_CALL,
];

export const isDeltaCallBucketTableTab = (tableMetricName: RiskTableTabs): boolean =>
  DELTA_CALL_BUCKET_TABLES.includes(tableMetricName);

/** Delta Call Bucket is a risk-table-only group-by. */
export const excludeDeltaCallBucketFromPositionLayers = (layers: TableLayerConstants[]): TableLayerConstants[] =>
  layers.filter((layer) => layer !== TableLayerConstants.DELTA_CALL_BUCKET);

/** Position table group-by layers: ClickHouse and risk-only buckets. */
export const resolvePositionTableLayers = (
  layers: TableLayerConstants[],
  shouldUseClickhouse: boolean,
): TableLayerConstants[] =>
  excludeDeltaCallBucketFromPositionLayers(excludeDeltaUnderlyingUnlessClickhouse(layers, shouldUseClickhouse));

export const getSegmentLayers = (): ComputedRef<TableLayerConstants[]> => {
  const { preferences } = useRiskPreferences();
  const { activeAnalysisSubstep } = useConstituentRiskAnalysisSteps();
  const { shouldUseClickhouse } = useFeatureFlag();

  const selectedLayers = computed(() => {
    const activeSubstep = activeAnalysisSubstep.value?.path;

    if (activeSubstep === RiskTabs.COMMODITY) {
      if (isDeltaCallBucketTableTab(preferences.value.commodity.tableMetricName)) {
        return preferences.value.commodity.deltaCallTableLayers;
      }

      if (
        preferences.value.commodity.tableMetricName === RiskTableTabs.COMMODITY_DELTA ||
        preferences.value.commodity.tableMetricName === RiskTableTabs.COMMODITY_GAMMA
      ) {
        return preferences.value.commodity.tableLayers;
      }

      if (preferences.value.commodity.tableMetricName === RiskTableTabs.SPOT_VOL_LADDER) {
        return preferences.value.commodity.ladderTableLayers;
      }
    }

    if (
      activeSubstep === RiskTabs.INTEREST_RATE &&
      preferences.value['interest-rate'].tableMetricName === RiskTableTabs.DV01_TENOR
    ) {
      return preferences.value['interest-rate'].tableLayers;
    }

    if (
      (activeSubstep === RiskTabs.INTEREST_RATE &&
        preferences.value['interest-rate'].tableMetricName === RiskTableTabs.SPOT_VOL_NORMAL_LADDER) ||
      preferences.value['interest-rate'].tableMetricName === RiskTableTabs.SPOT_VOL_BS_LADDER
    ) {
      return preferences.value['interest-rate'].ladderTableLayers;
    }

    if (activeSubstep === RiskTabs.CREDIT && preferences.value.credit.tableMetricName === RiskTableTabs.CS01_TENOR) {
      return preferences.value.credit.tableLayers;
    }

    if (activeSubstep === RiskTabs.EQUITY) {
      if (isDeltaCallBucketTableTab(preferences.value.equity.tableMetricName)) {
        return preferences.value.equity.deltaCallTableLayers;
      }

      if (preferences.value.equity.tableMetricName === RiskTableTabs.SPOT_VOL_LADDER) {
        return preferences.value.equity.ladderTableLayers;
      }
    }

    if (activeSubstep === RiskTabs.FX && preferences.value.fx.tableMetricName === RiskTableTabs.FX_DELTA) {
      return preferences.value.fx.tableLayers;
    }

    if (activeSubstep === RiskTabs.FX && preferences.value.fx.tableMetricName === RiskTableTabs.SPOT_VOL_LADDER) {
      return preferences.value.fx.ladderTableLayers;
    }

    return preferences.value.options.tableLayers;
  });

  return computed(() => excludeDeltaUnderlyingUnlessClickhouse(selectedLayers.value, shouldUseClickhouse.value));
};

/**
 * Formats a large number to make it more shorter and easier to read.
 * @param value Currency value to be formatted
 */
export const formatLongNumber = (value: number, ccy: Currency = Currency.USD): string => {
  let valToUse: string = value.toString();
  const suffixes = ['', ' k', ' M', ' B', ' T'];
  let suffixNum = 0;
  let shortValue = value;
  if (value >= 1000 || value <= -1000) {
    const stringValue = Math.round(Math.abs(value)).toString();

    // Counts how many groups of three digits in the value to determine which suffix to use
    suffixNum = Math.floor((stringValue.length - 1) / 3);

    // For loop to increase the precision of the output
    for (let precision = 3; precision >= 1; precision--) {
      shortValue = parseFloat((suffixNum !== 0 ? value / Math.pow(1000, suffixNum) : value).toPrecision(precision));
      const dotLessShortValue = `${shortValue}`.replace(/[^a-zA-Z 0-9]+/g, '');

      if (dotLessShortValue.length <= 3) {
        break;
      }
    }
  }

  if (shortValue % 1 !== 0) shortValue = parseFloat(shortValue.toFixed(DEFAULT_DECIMAL_PLACES));
  valToUse = shortValue.toString() + suffixes[suffixNum];

  return `${getCurrencySymbol(ccy)} ${valToUse}`;
};

export const getBasisPointDisplayName = (basisPoints: number): string => {
  return basisPoints !== 1 ? ` (x ${basisPoints.toLocaleString()})` : '';
};
export const getDV01DisplayName = (basisPoints: number): string => {
  return `DV01${getBasisPointDisplayName(basisPoints)}`;
};

export const getCS01DisplayName = (basisPoints: number): string => {
  return `CS01${getBasisPointDisplayName(basisPoints)}`;
};

export const useGetHistoricalTrackColor = () => {
  const { portfolioName, strategyName } = usePortfolioTree();
  const { portfolioTreeUnderAnalysis } = useItemUnderAnalysisGlobalState();
  const { colorMap } = useColorMap();
  const { getTrackSegment } = useConstituentRiskUtilities();

  const trackSegment = computed(() => getTrackSegment());
  const assetClassColors = computed(() => SettingsModule.colors.assetClass || {});
  const factorColors = computed(() => SettingsModule.colors.factor || {});
  const pinColors = computed(() => SettingsModule.colors.pin || {});

  return (name: string, idx: number): string => {
    if (
      name === portfolioName.value ||
      name === strategyName.value ||
      name === ExposureConstants.NET ||
      name === 'Subtotal'
    )
      return SettingsModule.portfolioColor;

    if (colorMap.value[name]) return colorMap.value[name].color;

    if (portfolioTreeUnderAnalysis.value) {
      const component = findTreeComponentByStrategyCode(portfolioTreeUnderAnalysis.value.portfolioTree, name);
      if (component && colorMap.value[component.portfolioTreeId])
        return colorMap.value[component.portfolioTreeId].color;
    }

    if (trackSegment.value === FilterConstants.ASSET_CLASS) {
      if (assetClassColors.value[name]) return assetClassColors.value[name];
    }

    if (trackSegment.value === FilterConstants.FACTOR) {
      if (factorColors.value[name]) return factorColors.value[name];
    }

    if (trackSegment.value !== FilterConstants.PORTFOLIO && trackSegment.value !== FilterConstants.STRATEGY) {
      const pins = Object.keys(pinColors.value);
      if (idx < pins.length - 1 && pinColors.value[pins[idx]]) return pinColors.value[pins[idx]];
    }

    return SettingsModule.defaultChildColor;
  };
};
